import type { EncryptedQuiz } from '@quiz/shared';

/**
 * The prefetched quiz and the player's token, kept in localStorage.
 *
 * This is what makes the app survive a wifi blip mid-round: the questions are
 * already on the phone, and a refresh or a dropped connection does not cost the
 * player their seat.
 */
const KEY = 'fast-quiz/session/v1';
const CODES_KEY = 'fast-quiz/codes/v1';

export interface PlayerSession {
  playerToken: string;
  playerId: string;
  callSign: string;
  eventId: string;
  eventName: string;
  quiz: EncryptedQuiz;
}

export function loadSession(): PlayerSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PlayerSession) : null;
  } catch {
    // Private browsing, cleared storage, a quota error -- none of which should
    // stop somebody joining. They just join again.
    return null;
  }
}

export function saveSession(session: PlayerSession): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    /* not fatal: the session lives in React state for this page load */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

const STAFF_KEY = 'fast-quiz/staff/v1';

export interface StaffSession {
  token: string;
  displayName: string;
  role: 'HOST' | 'MANAGER';
  eventId: string;
}

export const loadStaff = (): StaffSession | null => {
  try {
    const raw = localStorage.getItem(STAFF_KEY);
    return raw ? (JSON.parse(raw) as StaffSession) : null;
  } catch {
    return null;
  }
};
export const saveStaff = (s: StaffSession) => {
  try {
    localStorage.setItem(STAFF_KEY, JSON.stringify(s));
  } catch {
    /* not fatal */
  }
};
export const clearStaff = () => {
  try {
    localStorage.removeItem(STAFF_KEY);
  } catch {
    /* nothing to do */
  }
};

/**
 * Codes this phone has been given, per event.
 *
 * This is the whole of "who are you" in this app: no accounts, no sign-up, just
 * a browser that remembers what it was handed. It is what lets the home page say
 * "you are paid up for Thursday" before the quiz has started.
 *
 * It forgets if they switch phone, clear site data, or use a private window --
 * which is a real limit, not a bug to work around. The door's ticket list is the
 * authority on who has paid; this is only a convenience on the player's side.
 */
export type SavedCodes = Record<string, string>;

export function loadCodes(): SavedCodes {
  try {
    const raw = localStorage.getItem(CODES_KEY);
    return raw ? (JSON.parse(raw) as SavedCodes) : {};
  } catch {
    return {};
  }
}

export function saveCode(eventId: string, code: string): void {
  try {
    localStorage.setItem(CODES_KEY, JSON.stringify({ ...loadCodes(), [eventId]: code }));
  } catch {
    /* private window, full quota -- the player types it again, that is all */
  }
}

export function forgetCode(eventId: string): void {
  try {
    const codes = loadCodes();
    delete codes[eventId];
    localStorage.setItem(CODES_KEY, JSON.stringify(codes));
  } catch {
    /* nothing to do */
  }
}
